import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { PostUserUploadFilesDataTypes } from "../../../../Schema/postUserUploadFiles.types";
import { addFile } from "../../files/uploadedFiles/uploadedFiles";
import { userSubServiceFilesActions } from "../postUserSubServiceFiles/postUserSubServiceFiles";

export type PostUserUploadFileLoadAction = {
  type: string;
  payload: {
    file?: File;
    file_link?: string;
    isValidateNeeded?: boolean;
    file_ids?: number[];
    user_service_id?: number;
    isChat?: boolean;
    isAudio?: boolean;
  };
};

export type PostUserUploadFileData = {
  data: PostUserUploadFilesDataTypes;
};

const PostUserUploadFile = new ReduxFetchState<
  PostUserUploadFileData | null,
  {
    file?: File;
    file_link?: string;
    isValidateNeeded?: boolean;
    file_ids?: number[];
    user_service_id?: number;
    isChat?: boolean;
    isAudio?: boolean;
  },
  any
>("postUserUploadFile");

export function* watchPostUserUploadFile(action: PostUserUploadFileLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();
    const formData = new FormData();

    const { file, file_link, isValidateNeeded, file_ids, user_service_id, isChat, isAudio } =
      action.payload || {};

    if (file) {
      formData.append("file", file);
    }

    if (file_link) {
      formData.append("file_link", file_link);
    }

    const data: PostUserUploadFileData = yield axios({
      url: `/api/v1/ai/user/upload-file`,
      method: "POST",
      data: formData,
      headers: { lang, "Content-Type": "multipart/form-data" },
    });

    yield put(addFile({ ...data?.data?.user_file, isAudio }));
    yield put(PostUserUploadFile.actions.loadSuccess({ data: data.data }));

    if (!isValidateNeeded && !isChat) {
      yield put(
        userSubServiceFilesActions.load({
          file_ids,
          file_link,
          user_service_id,
        }),
      );
    }
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(PostUserUploadFile.actions.loadFailure(e.message));
  }
}

export function* postUserUploadFileSagas() {
  yield takeEvery(PostUserUploadFile.actionTypes.load, watchPostUserUploadFile);
}

export function usePostUserUploadFile() {
  const { loading, data, ...postUserUploadFileState } = useAppSelector(
    (state) => state.postUserUploadFile,
  );
  const dispatch = useAppDispatch();

  const dispatchPostUserUploadFile = useCallback(
    (data: {
      file?: File;
      file_link?: string;
      isValidateNeeded?: boolean;
      file_ids?: number[];
      user_service_id?: number;
      isChat?: boolean;
      isAudio?: boolean;
    }) => {
      dispatch(PostUserUploadFile.actions.load(data));
    },
    [dispatch],
  );

  return {
    ...postUserUploadFileState,
    dispatchPostUserUploadFile,
    postUserUploadFileLoading: loading,
    postUserUploadFileData: data,
  };
}

export const {
  actionTypes: postUserUploadFileActionsTypes,
  actions: postUserUploadFileActions,
  reducer: postUserUploadFileReducer,
} = PostUserUploadFile;
